import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import * as Sentry from "@sentry/react";
import {
  FC,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import Button from "@ingka/button";
import Hyperlink from "@ingka/hyperlink";
import IngkaLoading, { LoadingBall } from "@ingka/loading";
import arrowCloudIn from "@ingka/ssr-icon/paths/arrow-cloud-in";
import Text from "@ingka/text";
import { ForecastParcel, WorkloadDriversParcel } from "@pdpp/forecast-client";
import { OPPPermissions, OPPRoles } from "@pdpp/lib-planta";
import {
  ErrorBoundary,
  ErrorDisplay,
  ErrorPage,
  NOW_IT_LINK,
  OPP_MYIDENTITY_LINK,
  getLocationWithQueryParams,
  useAppVersion,
} from "@pdpp/lib-react";

import { css } from "./__generated-styled-system/css";
import { Auth } from "./api/auth";
import { MSAL_SCOPES, forecastApi } from "./api/fetch";
import { OrganisationSelect } from "./components/OrganisationSelect";
import { UnitSelect } from "./components/UnitSelect";
import { ROUTES } from "./config";
import { ModalStack } from "./countryActivities/common/ModalStack";
import { AppLayout, NavItemWithClick } from "./cwds/AppLayout";
import { userPermissions$ } from "./features/opp/state";
import { useGetUserPermissions } from "./features/opp/useGetUserPermissions";
import { useGetOrgStructure, useGetOrgUnits } from "./features/org/api";
import { countryCode$, orgId$, orgNodes$ } from "./features/org/state";
import {
  CODE_BY_COUNTRY_NAME,
  buildOrgNodeKey,
  getUnitFromOfficeLocation,
} from "./features/org/transforms";
import { clearHeadcountCache } from "./features/plan/api";
import { MainTab, setMainTab, tabState$ } from "./features/tabs/state";
import { useAnalytics } from "./hooks/useAnalytics";
import { useBaseNavigate } from "./hooks/useBaseNavigate";
import { Period } from "./plan/Period";
import { PlanBudget } from "./plan/PlanBudget";
import { PlanPeriod } from "./plan/PlanPeriod";
import { hasOppPermissions } from "./utils";
import { ADMIN_GROUP_IDS, OPP_AUTHZ } from "./utils/runtimeConfig";

const PlanMain = lazy(() =>
  import("./plan/PlanMain").then((module) => ({ default: module.PlanMain })),
);

const Publish = lazy(() =>
  import("./publications/Publish").then((module) => ({
    default: module.Publish,
  })),
);

const CountryActivities = lazy(() =>
  import("./countryActivities/ActivitiesWrapper").then((module) => ({
    default: module.ActivitiesWrapper,
  })),
);

const containerStyles = css({
  display: "grid",
  height: "100vh",
  width: "100vw",
  position: "relative",
  backgroundColor: "colourNeutral2",
});

const headerStyles = css({
  display: "grid",
  gridTemplateColumns: "1fr repeat(5, max-content)",
  justifyItems: "end",
  gap: "space300!",
  _first: { justifyItems: "start" },
  "@media (width < 760px)": {
    gridTemplateColumns: "auto auto 1fr",
    gap: "space200!",
  },
});

const buttonStyles = css({
  alignItems: "center",
});

const loadingStyles = css({
  height: "100vh",
  width: "100vw",
});

const contentStyles = css({
  display: "grid",
  gridTemplateRows: "1fr",
  height: "100vh",
});

const scrollContainerStyles = css({
  gridRow: "1 / -1",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
});

// Created once. Calling this during render returns a new component type and
// remounts the matched route on every parent render.
const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

const wrapperStyles = css({
  display: "grid",
  gridTemplateRows: "min-content min-content min-content",
  gridGap: "space50",
  paddingRight: "space550!",
  paddingLeft: "space550!",
  paddingTop: "space150!",
  height: "100%",
  "@media (width < 780px)": {
    paddingRight: "space250!",
    paddingLeft: "space250!",
  },
});

const ADMIN_COUNTRIES = "ALL_COUNTRIES" as const;
type UserCountry = string | typeof ADMIN_COUNTRIES;

const Loading: FC = () => {
  const [t] = useTranslation();

  return (
    <IngkaLoading
      className={loadingStyles}
      text={t("labels.loading")}
      labelTransitions={true}
    >
      <LoadingBall />
    </IngkaLoading>
  );
};

interface AuthState {
  readonly isInitialized: boolean;
  readonly hasValidToken: boolean;
}

export const App: FC = () => {
  const { accounts, inProgress } = useMsal();
  const location = useLocation();
  const { navigate, getBaseLocationQueryParams } = useBaseNavigate();
  const [t] = useTranslation();
  const [lang, setLang] = useState<string | "en">("");
  const [userCountry, setUserCountry] = useState<UserCountry>("");
  const { isError: isOppError, isLoading: isOppLoading } =
    useGetUserPermissions();

  const org = useGetOrgStructure({
    signal:
      accounts.length > 0 && inProgress === InteractionStatus.None
        ? AbortSignal.abort()
        : undefined,
    enabled: true,
    countryCode: userCountry === ADMIN_COUNTRIES ? "" : userCountry,
  });
  const { data: units, isLoading: isUnitsLoading } = useGetOrgUnits({
    enabled: true,
  });

  const [authState, setAuthState] = useState<AuthState>({
    isInitialized: false,
    hasValidToken: false,
  });

  const orgNode = orgId$.value;
  const orgNodes = orgNodes$.value;

  const orgNodeForForecast = useMemo((): string | undefined => {
    if (isUnitsLoading) return undefined;
    return buildOrgNodeKey(orgNodes, orgNode);
  }, [orgNodes, orgNode, isUnitsLoading]);

  const countryCodeForForecast = countryCode$.value;

  const currentVersion = import.meta.env["VERSION"];
  // Keep period/orgNode/view/etc. across deploys — full localStorage.clear() was
  // resetting users to current FY and "No unit selected" after weekend releases.
  useAppVersion({
    currentVersion,
  });

  useEffect(() => {
    const paths = Object.keys(ROUTES) as Array<keyof typeof ROUTES>;
    if (paths.find((p) => location.pathname.includes(ROUTES[p].path)) != null) {
      localStorage.setItem("path", location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const abortController = new AbortController();

    const handleAuth = async (): Promise<void> => {
      const popupConfig = {
        scopes: MSAL_SCOPES,
        prompt: "select_account",
        redirectUri: window.location.origin,
      };

      try {
        // If in iframe, always use popup
        if (window.parent !== window) {
          await Auth.get().login(popupConfig);
        } else {
          // Normal flow with redirect
          await Auth.get().login({
            scopes: MSAL_SCOPES,
            redirectStartPage: window.location.href,
          });
        }
        setAuthState({ isInitialized: true, hasValidToken: true });
      } catch (error: unknown) {
        if (
          error !== null &&
          typeof error === "object" &&
          "errorCode" in error &&
          error.errorCode === "monitor_window_timeout"
        ) {
          // Fallback to popup authentication
          await Auth.get().login(popupConfig);
          setAuthState({ isInitialized: true, hasValidToken: true });
        }
      }
    };

    if (accounts.length === 0 && inProgress === InteractionStatus.None) {
      handleAuth();
    } else if (accounts.length > 0 && inProgress === InteractionStatus.None) {
      setAuthState({ isInitialized: true, hasValidToken: true });
    }

    return () => {
      abortController.abort();
    };
  }, [accounts.length, inProgress]);

  useEffect(() => {
    const handleUserSelection = async (): Promise<void> => {
      const [user, userGroupIds] = await Promise.all([
        Auth.get().getUser(),
        Auth.get().getUserGroups("IAM"),
      ]);

      if (
        user === undefined ||
        user.profile.country === undefined ||
        user.profile.officeLocation === undefined
      ) {
        return;
      }
      setLang(user.profile.preferredLanguage ?? "en");
      const userUnit = getUnitFromOfficeLocation(user.profile.officeLocation);
      const noUnitSelected =
        orgId$.value === "-1" ||
        orgId$.value === undefined ||
        orgId$.value === "";

      const isAdmin =
        userGroupIds.some((element) =>
          ADMIN_GROUP_IDS.split(",").includes(element),
        ) ||
        userPermissions$?.value?.roles?.some(
          (role) => role === OPPRoles.OPP_DEVELOPER,
        );

      if (isAdmin === false) {
        setUserCountry(CODE_BY_COUNTRY_NAME.get(user.profile.country) ?? "");
      } else {
        setUserCountry(ADMIN_COUNTRIES);
      }

      // For regular users, set orgId based on their unit if none is selected
      if (userUnit !== undefined) {
        const selected = units
          ?.map((u) => {
            return u.units.find(
              (unit) =>
                unit.extId === userUnit.code && unit.extType === userUnit.type,
            );
          })
          .find((u) => u !== undefined);

        if (noUnitSelected && selected !== undefined) {
          orgId$.value = selected.id.toString();
        }
      }
    };

    if (units.length > 0 && userCountry === "") {
      handleUserSelection();
    }
  }, [units, userCountry, lang]);

  useAnalytics();

  const navItems = useMemo<Array<NavItemWithClick>>(
    () => [
      {
        label: t("plan.label"),
        href: ROUTES.PLAN.OVERVIEW.path,
        isActive: location.pathname.startsWith(ROUTES.PLAN.OVERVIEW.path),
        onClick: () => {
          const savedPlanTab = tabState$.value[MainTab.Plan];
          setMainTab(MainTab.Plan, savedPlanTab);

          // Navigate to the saved sub-tab
          let targetPath: string = ROUTES.PLAN.OVERVIEW.path;
          if (savedPlanTab === "plan") {
            targetPath = ROUTES.PLAN.PLAN.path;
          } else if (savedPlanTab === "activities") {
            targetPath = ROUTES.PLAN.ACTIVITIES.path;
          } else if (savedPlanTab === "methods") {
            targetPath = ROUTES.PLAN.METHODS.path;
          }

          navigate(getLocationWithQueryParams(targetPath));
        },
      },
      {
        label: t("activitiesTab.name"),
        href: ROUTES.COUNTRYACTIVITIES.path,
        isActive: location.pathname.startsWith(ROUTES.COUNTRYACTIVITIES.path),
        onClick: () => {
          const savedActivityTab = tabState$.value[MainTab.ActivityCatalogue];
          setMainTab(MainTab.ActivityCatalogue, savedActivityTab);

          // Navigate to the saved sub-tab
          let targetPath: string = ROUTES.COUNTRYACTIVITIES.LIST.path;
          if (savedActivityTab === "departmentMapping") {
            targetPath = ROUTES.COUNTRYACTIVITIES.DEPARTMENT_MAPPING.path;
          }

          navigate(getLocationWithQueryParams(targetPath));
        },
      },
      {
        label: t("forecast.workloadDriversLabel"),
        href: ROUTES.WORKLOAD_DRIVERS.path,
        isActive: location.pathname.startsWith(ROUTES.WORKLOAD_DRIVERS.path),
        onClick: () => {
          setMainTab(MainTab.WorkloadDrivers, "");
          navigate(getLocationWithQueryParams(ROUTES.WORKLOAD_DRIVERS.path));
        },
      },
      {
        label: t("labels.forecast"),
        href: ROUTES.FORECAST.path,
        isActive: location.pathname.startsWith(ROUTES.FORECAST.path),
        onClick: () => {
          setMainTab(MainTab.Forecast, "");
          navigate(getLocationWithQueryParams(ROUTES.FORECAST.path));
        },
      },
    ],

    [location.pathname, navigate, t],
  );

  const checkTokenExpiration = useCallback(async (): Promise<void> => {
    try {
      const account = accounts[0];
      if (!account) return;

      const token = await Auth.get().getToken({ scopes: MSAL_SCOPES });
      if (!token) {
        setAuthState({ isInitialized: false, hasValidToken: false });

        await Auth.get().login({
          scopes: MSAL_SCOPES,
          redirectStartPage: window.location.href,
        });
      }
    } catch (error) {
      console.error("Token check failed:", error);
      setAuthState({ isInitialized: false, hasValidToken: false });
    }
  }, [accounts]);

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        void checkTokenExpiration();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkTokenExpiration]);

  // Clear cache when forecast has been published (checked on route changes for lazy-loaded components)
  useEffect(() => {
    if (sessionStorage.getItem("clearPlantaHeadcountCache") === "true") {
      clearHeadcountCache();
      sessionStorage.removeItem("clearPlantaHeadcountCache");
    }
  }, [location.pathname]);

  // Clear cache on tab close
  useEffect(() => {
    const handleBeforeUnload = (): void => {
      clearHeadcountCache();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  if (
    !authState.isInitialized ||
    !authState.hasValidToken ||
    (!isOppError &&
      (inProgress !== InteractionStatus.None ||
        org.isLoading ||
        userCountry === ""))
  ) {
    return (
      <div className={containerStyles}>
        <Loading />
      </div>
    );
  }

  const isCountryActivities = location.pathname.includes(
    ROUTES.COUNTRYACTIVITIES.path,
  );
  const isWorkLoadDrivers = location.pathname.includes(
    ROUTES.WORKLOAD_DRIVERS.path,
  );
  const isForecast = location.pathname.startsWith(ROUTES.FORECAST.path);

  return (
    <div className={containerStyles}>
      {(OPP_AUTHZ && isOppError && !isOppLoading) || accounts.length === 0 ? (
        <ErrorPage
          title={t("errorPage.missingAccountTitle")}
          message={
            <Text tagName="span" textStyle="Body.M">
              <Trans
                i18nKey="errorPage.missingAccountMessage"
                components={{
                  MyIdentityLink: (
                    <Hyperlink
                      newWindow={true}
                      rel="noopener noreferrer nofollow"
                      url={OPP_MYIDENTITY_LINK}
                    />
                  ),
                }}
              />
            </Text>
          }
          videoUrl="/images/error-video.mp4"
          content={
            <>
              <Text tagName="span" textStyle="Body.M">
                <Trans
                  i18nKey="errorPage.nowItMessage"
                  components={{
                    itlink: (
                      <Hyperlink
                        newWindow={true}
                        rel="noopener noreferrer nofollow"
                        url={NOW_IT_LINK}
                      />
                    ),
                  }}
                />
              </Text>
            </>
          }
        />
      ) : (
        <Suspense fallback={<Loading />}>
          <div className={contentStyles}>
            <div className={scrollContainerStyles}>
              <ModalStack>
                <AppLayout navItems={navItems}>
                  {orgNode === "-1" ||
                  orgNode === undefined ||
                  orgNode === "" ? (
                    <UnitSelect units={units} />
                  ) : (
                    <div className={wrapperStyles}>
                      {!(
                        isCountryActivities ||
                        isWorkLoadDrivers ||
                        isForecast
                      ) && (
                        <div className={headerStyles}>
                          <OrganisationSelect
                            nodes={orgNodes}
                            orgId={orgNode}
                          />

                          {hasOppPermissions(
                            OPPPermissions.WORKLOAD_HEADCOUNT_READ,
                          ) && <PlanPeriod />}
                          {hasOppPermissions(
                            OPPPermissions.WORKLOAD_BUDGET_READ,
                          ) && <PlanBudget />}
                          <Period />
                          {hasOppPermissions(
                            OPPPermissions.WORKLOAD_HEADCOUNT_WRITE,
                          ) && (
                            <Button
                              className={buttonStyles}
                              onClick={() => {
                                navigate(
                                  getLocationWithQueryParams(
                                    ROUTES.PUBLISH.path,
                                  ),
                                );
                              }}
                              type="primary"
                              text={t("publications.publish")}
                              ssrIcon={arrowCloudIn}
                              size="small"
                            />
                          )}
                        </div>
                      )}

                      <ErrorBoundary
                        fallback={ErrorDisplay}
                        videoUrl="/images/error-video.mp4"
                        helpdeskUrl={NOW_IT_LINK}
                      >
                        <SentryRoutes>
                          <Route
                            path={ROUTES.PUBLISH.path}
                            element={<Publish />}
                          />
                          <Route
                            path={ROUTES.PLAN.all}
                            element={<PlanMain />}
                          />
                          <Route
                            path={ROUTES.COUNTRYACTIVITIES.all}
                            element={<CountryActivities />}
                          />
                          <Route
                            path={ROUTES.WORKLOAD_DRIVERS.all}
                            element={
                              <WorkloadDriversParcel
                                forecastApi={forecastApi}
                                countryCode={countryCode$.value ?? ""}
                                language={lang}
                              />
                            }
                          />

                          <Route
                            path={ROUTES.FORECAST.all}
                            element={
                              <ForecastParcel
                                forecastApi={forecastApi}
                                orgNode={orgNodeForForecast ?? ""}
                                countryCode={countryCodeForForecast}
                                language={lang}
                              />
                            }
                          />
                          <Route
                            path="*"
                            element={
                              <Navigate
                                to={getBaseLocationQueryParams(
                                  ROUTES.PLAN.OVERVIEW.path,
                                )}
                                replace
                              />
                            }
                          />
                        </SentryRoutes>
                      </ErrorBoundary>
                    </div>
                  )}
                </AppLayout>
              </ModalStack>
            </div>
          </div>
        </Suspense>
      )}
    </div>
  );
};
